import React, { useState, useMemo } from 'react';

// Category groups from aiAnalysisService.js
const CATEGORY_GROUPS = {
  '💼 Business & Finance': [1, 2, 3, 4, 5, 6, 7, 8, 9],
  '📈 Marketing & Sales': [10, 11, 12, 13, 14, 15, 16, 17],
  '💻 Technology': [18, 19, 20, 21, 22, 23],
  '🎓 Education & Career': [24, 25, 26, 27, 28, 29, 30],
  '🎮 Entertainment': [31, 32, 33, 34, 35, 36, 37, 38],
  '📰 News & Media': [39, 40, 41, 42],
  '💪 Health & Beauty': [43, 44, 45, 46, 47],
  '🏠 Lifestyle & Family': [48, 49, 50, 51, 52, 53, 54],
  '🔬 Specialized': [55, 56, 57, 58],
  '🔞 Adult Entertainment': [59, 60],
  '📱 Telegram Specific': [61, 62, 63],
  '⚙️ System': [98, 99]
};

// Complete categories mapping
const CATEGORIES = {
  // Business & Finance
  1: 'Banking & Investments',
  2: 'Cryptocurrency & Trading',
  3: 'NFT & Digital Assets',
  4: 'Startups & Business', 
  5: 'Entrepreneurship',
  6: 'Economics & Finance',
  7: 'Accounting & Legal',
  8: 'Real Estate',
  9: 'Insurance & Tax',

  // Marketing & Sales
  10: 'Marketing & Advertising',
  11: 'SMM & Social Media',
  12: 'SEO & Digital Marketing',
  13: 'Affiliate & CPA Marketing',
  14: 'Traffic Arbitrage',
  15: 'E-commerce & Sales',
  16: 'Deals & Offers',
  17: 'Services & B2B',

  // Technology
  18: 'IT & Software Development',
  19: 'Web Development & Design',
  20: 'Mobile Technology & Apps',
  21: 'AI & Data Science',
  22: 'Cybersecurity',
  23: 'Gadgets & Electronics',

  // Education & Career
  24: 'Education & Courses',
  25: 'Online Learning & Webinars',
  26: 'Employment & Jobs',
  27: 'Career Development & Resume',
  28: 'Professional Development',
  29: 'Conferences & Events',
  30: 'Languages & Linguistics',

  // Entertainment
  31: 'Humor & Entertainment',
  32: 'Memes & Viral Content',
  33: 'Gaming & Esports',
  34: 'Movies & TV Shows',
  35: 'Music & Audio',
  36: 'Books & Literature',
  37: 'Art & Photography',
  38: 'Comics & Anime',

  // News & Media
  39: 'News & Media',
  40: 'Politics & Society',
  41: 'Blogs & Personal Content',
  42: 'Journalism & Reporting',

  // Health & Beauty
  43: 'Health & Medicine',
  44: 'Mental Health & Psychology',
  45: 'Beauty & Skincare',
  46: 'Fashion & Style',
  47: 'Sports & Fitness',

  // Lifestyle & Family
  48: 'Food & Cooking',
  49: 'Travel & Tourism',
  50: 'Family & Parenting',
  51: 'Relationships & Dating',
  52: 'Hobbies & Crafts',
  53: 'Pets & Animals',
  54: 'Automotive & Transport',

  // Specialized
  55: 'Science & Research',
  56: 'Religion & Spirituality',
  57: 'Local & Regional Communities',
  58: 'Charity & Social Causes',

  // Adult Entertainment
  59: 'Gambling & Casino',
  60: 'Adult Content & Dating (18+)',

  // Telegram Specific
  61: 'Telegram Tools & Bots',
  62: 'Sticker Catalogs',
  63: 'Channel Promotion',

  // System
  98: 'Spam & Low Quality',
  99: 'Other & Uncategorized'
};

const CategorySelect = ({ 
  value, 
  onChange, 
  placeholder = "All Categories",
  multiple = false,
  className = "",
  searchable = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return CATEGORIES;
    
    const filtered = {};
    Object.entries(CATEGORIES).forEach(([id, name]) => {
      if (name.toLowerCase().includes(searchTerm.toLowerCase())) {
        filtered[id] = name;
      }
    });
    return filtered;
  }, [searchTerm]);

  // Get filtered groups
  const filteredGroups = useMemo(() => {
    const filtered = {};
    Object.entries(CATEGORY_GROUPS).forEach(([groupName, categoryIds]) => {
      const filteredCategoryIds = categoryIds.filter(id => 
        filteredCategories[id]
      );
      if (filteredCategoryIds.length > 0) {
        filtered[groupName] = filteredCategoryIds;
      }
    });
    return filtered;
  }, [filteredCategories]);

  const handleSelectChange = (e) => {
    const selectedValue = e.target.value;
    if (multiple) {
      // Handle multiple selection logic if needed
      onChange(selectedValue);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {searchable && (
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      )}
      
      <select 
        value={value || ''} 
        onChange={handleSelectChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">{placeholder}</option>
        
        {Object.entries(filteredGroups).map(([groupName, categoryIds]) => (
          <optgroup key={groupName} label={groupName}>
            {categoryIds.map(categoryId => (
              <option key={categoryId} value={categoryId}>
                {CATEGORIES[categoryId]}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};

export default CategorySelect;
export { CATEGORIES, CATEGORY_GROUPS };
